import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Lock, Database, FileText, UserCheck, AlertCircle } from 'lucide-react';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GDPR = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/general-settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const companyName = settings?.company_name || appSettings?.company_name || 'OddsChamp';
  const contactEmail = settings?.contact_email || 'support@oddschamp.com';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {user ? <Layout /> : null}
      
      <div className={`${user ? '' : 'pt-20'} pb-20 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-blue-500/20 rounded-full">
                <Shield className="w-12 h-12 text-blue-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              GDPR Compliance
            </h1>
            <p className="text-xl text-gray-300">
              General Data Protection Regulation
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Last Updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Quick Navigation */}
          <Card className="glass-dark border-white/10 mb-8">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quick Navigation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <a href="#data-controller" className="text-blue-400 hover:text-blue-300 text-sm">→ Data Controller Information</a>
                <a href="#data-collected" className="text-blue-400 hover:text-blue-300 text-sm">→ Data We Collect</a>
                <a href="#legal-basis" className="text-blue-400 hover:text-blue-300 text-sm">→ Legal Basis for Processing</a>
                <a href="#data-usage" className="text-blue-400 hover:text-blue-300 text-sm">→ How We Use Your Data</a>
                <a href="#data-sharing" className="text-blue-400 hover:text-blue-300 text-sm">→ Data Sharing & Transfers</a>
                <a href="#your-rights" className="text-blue-400 hover:text-blue-300 text-sm">→ Your GDPR Rights</a>
                <a href="#data-retention" className="text-blue-400 hover:text-blue-300 text-sm">→ Data Retention</a>
                <a href="#security" className="text-blue-400 hover:text-blue-300 text-sm">→ Security Measures</a>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Introduction */}
            <Card className="glass-dark border-white/10">
              <CardContent className="pt-6">
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed">
                    {companyName} is committed to protecting your personal data and respecting your privacy rights under the 
                    General Data Protection Regulation (GDPR) and other applicable data protection laws. This GDPR Compliance 
                    statement explains how we collect, use, store, and protect your personal information in accordance with 
                    EU data protection standards.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data Controller */}
            <Card id="data-controller" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <UserCheck className="w-5 h-5 text-blue-400" />
                  1. Data Controller Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-3">
                  <p><strong className="text-white">Data Controller:</strong> {companyName}</p>
                  <p><strong className="text-white">Contact Email:</strong> {contactEmail}</p>
                  <p><strong className="text-white">Service:</strong> Online betting tips and analytics platform</p>
                  <p className="text-sm">
                    For any questions regarding your personal data or to exercise your GDPR rights, please contact 
                    our Data Protection Officer at the email address above.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data We Collect */}
            <Card id="data-collected" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Database className="w-5 h-5 text-blue-400" />
                  2. Personal Data We Collect
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Account Information:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Full name</li>
                      <li>Email address</li>
                      <li>Password (encrypted)</li>
                      <li>Profile picture (optional)</li>
                      <li>Date of birth</li>
                      <li>Country of residence</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Betting Activity Data:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Betting history and records</li>
                      <li>Stakes, odds, and outcomes</li>
                      <li>Betting strategies and preferences</li>
                      <li>Tips purchased and subscribed services</li>
                      <li>Performance statistics</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Payment Information:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Subscription plan and status</li>
                      <li>Payment transaction history</li>
                      <li>PayPal email (for tipsters)</li>
                      <li>Stripe customer ID (stored by Stripe)</li>
                      <li>Payment card details (processed and stored by Stripe, not by us)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Technical Data:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>IP address</li>
                      <li>Browser type and version</li>
                      <li>Device information</li>
                      <li>Session data and cookies</li>
                      <li>Usage analytics and patterns</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Communication Data:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Support tickets and correspondence</li>
                      <li>Feedback and reviews</li>
                      <li>Email preferences</li>
                      <li>Notification settings</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal Basis */}
            <Card id="legal-basis" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-blue-400" />
                  3. Legal Basis for Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Contract Performance:</h4>
                    <p>Processing necessary to provide our services, process payments, and fulfill our contractual obligations to you.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Legitimate Interest:</h4>
                    <p>Processing for improving our services, fraud prevention, security, analytics, and marketing communications 
                    (where we have assessed that our interests do not override your rights).</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Consent:</h4>
                    <p>Processing based on your explicit consent for specific purposes such as marketing communications, 
                    cookies, and optional data sharing.</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">Legal Obligation:</h4>
                    <p>Processing required to comply with legal requirements, such as tax obligations, anti-money laundering 
                    regulations, and law enforcement requests.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Usage */}
            <Card id="data-usage" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Database className="w-5 h-5 text-blue-400" />
                  4. How We Use Your Personal Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300">
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>To create and manage your account</li>
                    <li>To provide betting tracking and analytics services</li>
                    <li>To process payments and subscriptions</li>
                    <li>To deliver tips, predictions, and content you've purchased</li>
                    <li>To send service-related notifications and updates</li>
                    <li>To provide customer support</li>
                    <li>To improve and personalize our services</li>
                    <li>To detect and prevent fraud, abuse, and security issues</li>
                    <li>To analyze usage patterns and generate insights</li>
                    <li>To comply with legal obligations</li>
                    <li>To send marketing communications (with your consent)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card id="data-sharing" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                  5. Data Sharing & International Transfers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">We Share Your Data With:</h4>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li><strong className="text-white">Payment Processors:</strong> Stripe and PayPal for secure payment processing (they have their own GDPR-compliant policies)</li>
                      <li><strong className="text-white">Email Service Providers:</strong> For sending transactional and marketing emails (with consent)</li>
                      <li><strong className="text-white">Cloud Service Providers:</strong> For hosting and data storage (AWS, MongoDB Atlas, etc.)</li>
                      <li><strong className="text-white">Analytics Providers:</strong> For usage analytics and service improvement</li>
                      <li><strong className="text-white">Legal Authorities:</strong> When required by law or to protect rights and safety</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">International Data Transfers:</h4>
                    <p>Your data may be transferred to and processed in countries outside the European Economic Area (EEA). 
                    We ensure appropriate safeguards are in place, including:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>EU-US Data Privacy Framework compliance</li>
                      <li>Standard Contractual Clauses (SCCs)</li>
                      <li>Adequacy decisions by the EU Commission</li>
                    </ul>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-sm">
                      <strong className="text-white">We Never:</strong> Sell your personal data to third parties or use it for 
                      purposes incompatible with those disclosed in this statement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card id="your-rights" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Shield className="w-5 h-5 text-blue-400" />
                  6. Your GDPR Rights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-4">
                  <p>Under GDPR, you have the following rights regarding your personal data:</p>

                  <div className="space-y-3">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Access</h5>
                      <p className="text-sm">Request a copy of your personal data we hold about you.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Rectification</h5>
                      <p className="text-sm">Request correction of inaccurate or incomplete personal data.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Erasure ("Right to be Forgotten")</h5>
                      <p className="text-sm">Request deletion of your personal data in certain circumstances.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Restriction of Processing</h5>
                      <p className="text-sm">Request limitation of how we use your data in certain situations.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Data Portability</h5>
                      <p className="text-sm">Receive your personal data in a structured, commonly used format, or request transfer to another controller.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Object</h5>
                      <p className="text-sm">Object to processing based on legitimate interests or direct marketing.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Withdraw Consent</h5>
                      <p className="text-sm">Withdraw consent for processing based on consent at any time.</p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="font-semibold text-white mb-1">Right to Lodge a Complaint</h5>
                      <p className="text-sm">File a complaint with your local data protection authority if you believe your rights have been violated.</p>
                    </div>
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mt-4">
                    <p className="text-sm">
                      <strong className="text-white">To Exercise Your Rights:</strong> Contact us at {contactEmail}. 
                      We will respond to your request within one month. You can also manage many settings directly in your account profile.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card id="data-retention" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Database className="w-5 h-5 text-blue-400" />
                  7. Data Retention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-3">
                  <p>We retain your personal data only for as long as necessary to fulfill the purposes outlined in this statement:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Account Data:</strong> Retained while your account is active and for 2 years after account closure (unless legal obligations require longer retention)</li>
                    <li><strong className="text-white">Betting History:</strong> Retained for 7 years for tax and regulatory compliance purposes</li>
                    <li><strong className="text-white">Payment Records:</strong> Retained for 7 years for accounting and legal requirements</li>
                    <li><strong className="text-white">Marketing Data:</strong> Retained until you withdraw consent or for 2 years of inactivity</li>
                    <li><strong className="text-white">Technical Logs:</strong> Retained for 90 days for security and troubleshooting</li>
                  </ul>
                  <p className="text-sm mt-4">
                    After the retention period expires, we securely delete or anonymize your personal data.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Security */}
            <Card id="security" className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Lock className="w-5 h-5 text-blue-400" />
                  8. Security Measures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-3">
                  <p>We implement appropriate technical and organizational measures to protect your personal data:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
                    <li>Password hashing using industry-standard algorithms</li>
                    <li>Regular security audits and vulnerability assessments</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Secure cloud infrastructure with redundancy</li>
                    <li>Employee training on data protection</li>
                    <li>Incident response and data breach notification procedures</li>
                  </ul>
                  <p className="text-sm mt-4">
                    While we strive to protect your personal data, no method of transmission or storage is 100% secure. 
                    Please use strong passwords and keep your account credentials confidential.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-blue-400" />
                  9. Cookies and Tracking Technologies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-3">
                  <p>We use cookies and similar technologies to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Maintain your login session</li>
                    <li>Remember your preferences and settings</li>
                    <li>Analyze usage patterns and improve our services</li>
                    <li>Provide personalized content and recommendations</li>
                  </ul>
                  <p className="text-sm mt-4">
                    You can manage cookie preferences through your browser settings. Note that disabling certain cookies may 
                    affect the functionality of our services.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Children's Privacy */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                  10. Children's Privacy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300">
                  <p>
                    Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
                    data from children. If you believe we have inadvertently collected data from a child, please contact us 
                    immediately at {contactEmail}, and we will delete such information promptly.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-blue-400" />
                  11. Updates to This Statement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300">
                  <p>
                    We may update this GDPR Compliance statement from time to time to reflect changes in our practices or 
                    legal requirements. We will notify you of significant changes via email or through a prominent notice 
                    on our platform. Your continued use of our services after such modifications constitutes acceptance of 
                    the updated statement.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <UserCheck className="w-5 h-5 text-blue-400" />
                  12. Contact & Data Protection Officer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 space-y-3">
                  <p>
                    If you have any questions, concerns, or requests regarding this GDPR Compliance statement or our data 
                    protection practices, please contact our Data Protection Officer:
                  </p>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p><strong className="text-white">Email:</strong> {contactEmail}</p>
                    <p><strong className="text-white">Company:</strong> {companyName}</p>
                    <p><strong className="text-white">Response Time:</strong> Within 30 days</p>
                  </div>
                  <p className="text-sm">
                    If you are not satisfied with our response, you have the right to lodge a complaint with your local 
                    data protection supervisory authority.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mt-12">
            {user ? (
              <>
                <Button
                  onClick={() => navigate('/profile')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Manage Your Data
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Account
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  Back to Home
                </Button>
              </>
            )}
          </div>

          {/* Related Links */}
          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm mb-3">Related Documents:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 text-sm">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms-of-use" className="text-blue-400 hover:text-blue-300 text-sm">
                Terms of Use
              </Link>
            </div>
          </div>
        </div>
      </div>

      {!user && <Footer />}
    </div>
  );
};

export default GDPR;
